import {Button, TextField, MenuItem, Grid, CircularProgress} from '@material-ui/core';
import React, {useState, useRef} from 'react';
import './VerifyCriticalInformationForm.sass';
import OutlinedSelect from 'Shared/components/OutlinedSelect';
import {CaseNumber, matchCaseNumber} from 'ApplicationEntry/models/CaseNumber';
import {AgentNumber, matchAgentNumber} from 'ApplicationEntry/models/AgentNumber';
import * as V from 'ApplicationEntry/ducks/verifyCriticalInformation';
import Alert from 'Shared/components/Alert';
import {useDispatch} from 'react-redux';
import {PolicyNumber, matchPolicyNumber} from 'ApplicationEntry/models/PolicyNumber';
import {ValidAgentList, AgentList} from 'ApplicationEntry/models/AgentList';
import * as VCI from 'ApplicationEntry/models/NewYorkVerifyCriticalInformation';
import {EmptyString} from 'Core/models/EmptyString';
import {NewYorkBillingAgent} from 'ApplicationEntry/models/NewYorkBillingAgent';
import {NewYorkApplicantDemographics} from 'ApplicationEntry/models/NewYorkApplicantDemographics';
import {NewYorkCaseDetails} from 'ApplicationEntry/models/NewYorkCaseDetails';

type NewYorkVerifyCriticalInformationFormProps = {
  agentList: AgentList;
  applicantDemographics: NewYorkApplicantDemographics;
  billingAgent: NewYorkBillingAgent;
  caseDetails: NewYorkCaseDetails;
  onCancel: () => void;
  verifyCriticalInformation: VCI.NewYorkVerifyCriticalInformation;
};

export const NewYorkVerifyCriticalInformationForm = ({
  agentList,
  applicantDemographics,
  billingAgent,
  caseDetails,
  verifyCriticalInformation,
  onCancel,
}: NewYorkVerifyCriticalInformationFormProps) => {
  const dispatch = useDispatch();
  const {account: accountNumber, caseNumber, controlNumber, transit} = billingAgent;
  const {policyNumber} = caseDetails;
  const {name: applicant} = applicantDemographics;
  const originalVerifyCriticalInformation = VCI.ValidNewYorkVerifyCriticalInformation.create(
    caseNumber,
    new EmptyString(),
    transit,
    accountNumber,
    controlNumber,
    applicant,
    agentList
  );
  const originalInputData = {accountNumber, applicant, caseNumber, controlNumber, transit, agentList};
  const getAgentNumber = (agentNumber: AgentNumber) =>
    matchAgentNumber({
      Unassigned: () => '',
      Loading: () => '',
      Loaded: () => '',
      Error: () => '',
      UserAssignedAgentNumber: agentNumber => agentNumber,
      AgentNumberError: () => '',
    })(agentNumber);
  const getCaseNumber = (caseNumber: CaseNumber) =>
    matchCaseNumber({
      Loading: () => '',
      Loaded: () => '',
      Unassigned: () => '',
      UserAssignedCaseNumber: caseNumber => caseNumber,
      Error: () => '',
      CaseNumberError: () => '',
    })(caseNumber)!;
  const getPolicyNumber = (policyNumber: PolicyNumber) =>
    matchPolicyNumber({
      Unassigned: () => '',
      Loaded: policyNumber => policyNumber,
      Loading: () => '',
      Error: () => '',
      UserAssignedPolicyNumber: policyNumber => policyNumber,
      PolicyNumberValidating: () => '',
      Reserved: () => '',
      PolicyNumberError: () => '',
    })(policyNumber);
  const changeInformation = (key: string, value: string | string[], displayName: string) => {
    let hasChanged;

    switch (key) {
      case 'accountNumber':
      case 'applicant':
      case 'controlNumber':
      case 'transit': {
        hasChanged = value !== '' && (value as string).toLowerCase() !== originalInputData[key].value.toLowerCase();
        break;
      }
      case 'caseNumber':
        hasChanged =
          value !== '' && (value as string).toLowerCase() !== getCaseNumber(originalInputData[key]).toLowerCase();
        break;
      case 'agentList':
        hasChanged = agentList.agentList.size > 0 && value.length !== agentList.agentList.size;
        break;
      default:
        hasChanged = false;
    }

    if (!hasChanged) {
      setChangedFields(changedFields.filter(field => field !== displayName));
    } else {
      if (!changedFields.includes(displayName)) {
        setChangedFields([...changedFields, displayName]);
      }
    }
  };
  const [selectedAgentNumber, setSelectedAgentNumber] = useState<string[]>([]);
  const [changedFields, setChangedFields] = useState<string[]>([]);
  const handleChangeAgentNumber = (event: React.ChangeEvent<{value: unknown}>) => {
    const agentNumberList = event.target.value as string[];

    setSelectedAgentNumber(agentNumberList);

    changeInformation('agentList', agentNumberList, 'agent list');

    const newAgentList = agentList.agentList.filter(agent =>
      agentNumberList.includes(getAgentNumber(agent.agentNumber)!)
    );

    dispatch(V.changeAgentList(ValidAgentList.create(newAgentList)));
  };
  const textInput = useRef<HTMLLabelElement | null>(null);
  const isValid = VCI.isVerified(originalVerifyCriticalInformation, verifyCriticalInformation);

  return (
    <form
      noValidate
      autoComplete="off"
      className="verify-critical-information"
      onSubmit={event => {
        event.preventDefault();
        onCancel();
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            id="policyNumber"
            label="Policy Number"
            variant="outlined"
            margin="normal"
            value={getPolicyNumber(policyNumber)}
            fullWidth
            disabled
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            id="applicant"
            label="Applicant"
            variant="outlined"
            placeholder="Enter Applicant"
            margin="normal"
            fullWidth
            value={verifyCriticalInformation.applicant.value}
            onChange={event => {
              dispatch(V.changeApplicant(event.target.value));
              changeInformation('applicant', event.target.value, 'applicant');
            }}
            disabled={applicant.value === ''}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <OutlinedSelect
            id="agentList"
            variant="outlined"
            disabled={agentList.agentList.size === 0}
            multiple
            value={selectedAgentNumber}
            onChange={handleChangeAgentNumber}
            className="verify-critical-information--select"
            label="Agent List"
          >
            {agentList.agentList.map(agentInformation => {
              const agentNumber = getAgentNumber(agentInformation.agentNumber);

              return (
                <MenuItem key={agentNumber} value={agentNumber}>
                  {agentNumber}
                </MenuItem>
              );
            })}
          </OutlinedSelect>
        </Grid>
        <Grid item xs={12} md={6}>
          {matchCaseNumber({
            Loading: () => (
              <TextField
                label="Case #"
                variant="filled"
                placeholder="Case Number"
                value={''}
                InputProps={{
                  endAdornment: <CircularProgress className="verify-critical-information--loading-case-number-icon" />,
                }}
                disabled={getCaseNumber(caseNumber) === ''}
              />
            ),
            Loaded: inputCaseNumber => (
              <TextField
                label="Case #"
                variant="outlined"
                placeholder="Case Number"
                value={inputCaseNumber}
                onChange={event => {
                  textInput.current?.focus();
                  changeInformation('caseNumber', event.target.value, 'case number');
                  dispatch(V.changeUserCaseNumber(event.target.value));
                }}
                onBlur={event => dispatch(V.getCaseNameByCaseNumber(event.target.value))}
              />
            ),
            Unassigned: () => (
              <TextField
                label="Case #"
                variant="filled"
                placeholder="Case Number"
                value={''}
                onChange={event => {
                  textInput.current?.focus();
                  changeInformation('caseNumber', event.target.value, 'case number');
                  dispatch(V.changeUserCaseNumber(event.target.value));
                }}
              />
            ),
            UserAssignedCaseNumber: inputCaseNumber => (
              <TextField
                label="Case #"
                variant="outlined"
                placeholder="Enter Case Number"
                margin="normal"
                fullWidth
                value={inputCaseNumber}
                onChange={event => {
                  changeInformation('caseNumber', event.target.value, 'case number');
                  dispatch(V.changeUserCaseNumber(event.target.value));
                }}
                onBlur={event => dispatch(V.getCaseNameByCaseNumber(event.target.value))}
                id="user-assigned-verify-critical-information-case-number"
                disabled={getCaseNumber(caseNumber) === ''}
              />
            ),
            Error: () => <Alert message="An Error Ocurred While Getting Case Number." />,
            CaseNumberError: inputCaseNumber => (
              <TextField
                error
                id="outlined-error-helper-text"
                label="Case #"
                defaultValue={inputCaseNumber}
                helperText="Not a Valid Case Number"
                variant="outlined"
                margin="normal"
                fullWidth
                value={inputCaseNumber}
                onChange={event => {
                  changeInformation('caseNumber', event.target.value, 'case number');
                  dispatch(V.changeUserCaseNumber(event.target.value));
                }}
                placeholder="Enter Case Number"
              />
            ),
          })(verifyCriticalInformation.caseNumber)}
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            id="controlNumber"
            label="Control #"
            variant="outlined"
            placeholder="Enter Control Number"
            margin="normal"
            fullWidth
            value={verifyCriticalInformation.controlNumber.value}
            onChange={event => {
              dispatch(V.changeControlNumber(event.target.value));
              changeInformation('controlNumber', event.target.value, 'control number');
            }}
            disabled={controlNumber.value === ''}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            id="transit"
            label="Transit"
            variant="outlined"
            placeholder="Enter Transit"
            margin="normal"
            fullWidth
            value={verifyCriticalInformation.transit.value}
            onChange={event => {
              dispatch(V.changeTransit(event.target.value));
              changeInformation('transit', event.target.value, 'transit');
            }}
            disabled={transit.value === ''}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            id="accountNumber"
            label="Account #"
            variant="outlined"
            placeholder="Enter Account Number"
            margin="normal"
            fullWidth
            value={verifyCriticalInformation.accountNumber.value}
            onChange={event => {
              dispatch(V.changeAccountNumber(event.target.value));
              changeInformation('accountNumber', event.target.value, 'account number');
            }}
            disabled={accountNumber.value === ''}
          />
        </Grid>
      </Grid>
      <div className="verify-critical-information__output">
        {changedFields.length > 0 && (
          <>
            <p className="verify-critical-information__output--error">
              The following fields have changed: {changedFields.join(', ')}.
            </p>
            <p>
              Correct the input to proceed. Press{' '}
              <span className="verify-critical-information__output--action-name">"Cancel"</span> to exit this dialog.
            </p>
          </>
        )}
        {!isValid && (
          <p className="verify-critical-information__output--error">Please, enter the required information.</p>
        )}
      </div>
      <div className="verify-critical-information__actions">
        {
          <Button
            variant="contained"
            type="submit"
            className="verify-critical-information__actions--button"
            color="primary"
            disabled={changedFields.length > 0 || !isValid}
          >
            OK
          </Button>
        }
        <Button
          className="verify-critical-information__actions--button"
          color="inherit"
          variant="contained"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default NewYorkVerifyCriticalInformationForm;
